import { useCallback, useEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { noop } from '@sweetgreen/garnish';

import { useIsCustomerSessionReady } from '@order/hooks';

import { useDeliveryInFlightQuery } from './DeliveryInFlight.generated';
import { useDeliveryInFlightFeatureFlag } from './useDeliveryInFlightFeatureFlag';

// Fetches in-flight delivery orderId and controls modal visibility.
type HookProps = { readonly syncWithSession?: boolean };
export const useDeliveryInFlightWarning = (props?: HookProps) => {
  const shouldUseThisHook = !useDeliveryInFlightFeatureFlag();

  // ───────── Refresh In-Flight OrderId on Page Mount ──────────
  const [ordersResponse, refetchOrders] = useDeliveryInFlightQuery({
    variables: { input: { page: 1, take: 3 } },
    pause: true,
  });
  const isRenderedOnScreen = useIsFocused();
  const isCustomerSessionReady = useIsCustomerSessionReady();

  useEffect(() => {
    if (shouldUseThisHook && isRenderedOnScreen && isCustomerSessionReady) {
      refetchOrders();
    }
  }, [
    shouldUseThisHook,
    isRenderedOnScreen,
    isCustomerSessionReady,
    refetchOrders,
  ]);

  // ─────────────── In Flight Delivery Order Id ───────────────
  const ordersResponseData = ordersResponse.data?.orders;
  const isSuccess = ordersResponseData?.__typename === 'OrdersResponseSuccess';
  const orders = isSuccess ? ordersResponseData.orders : [];
  const inFlightDeliveryOrder = orders.find(
    ({ isInFlightDelivery }) => isInFlightDelivery,
  )?.id;

  // ──────────────── Modal Visibility Controls ────────────────
  const [deliveryInFlightWarning, setDeliveryInFlightWarning] = useState(false);
  const showDeliveryInFlightWarning = useCallback(() => {
    setDeliveryInFlightWarning(true);
  }, []);
  const hideDeliveryInFlightWarning = useCallback(() => {
    setDeliveryInFlightWarning(false);
  }, []);

  // ────────────── Sync Visibility with Session ───────────────
  const syncWithSession = props?.syncWithSession ?? false;

  useEffect(() => {
    if (!syncWithSession || !shouldUseThisHook) return;
    setDeliveryInFlightWarning(Boolean(inFlightDeliveryOrder));
  }, [inFlightDeliveryOrder, syncWithSession, shouldUseThisHook]);

  if (!shouldUseThisHook) {
    return {
      inFlightDeliveryOrder: undefined,
      deliveryInFlightWarning: false,
      showDeliveryInFlightWarning: noop,
      hideDeliveryInFlightWarning: noop,
      setDeliveryInFlightWarning: noop,
    };
  }

  return {
    inFlightDeliveryOrder,
    deliveryInFlightWarning,
    showDeliveryInFlightWarning,
    hideDeliveryInFlightWarning,
    setDeliveryInFlightWarning,
  };
};
